import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import Reveal from '../components/Reveal';
import logoIcon from '../assets/icon1-no-bg.png';

const AdminDashboard = () => {
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAppt, setSelectedAppt] = useState(null);
  const [revenueInput, setRevenueInput] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Login form state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user?.email === 'admin@tankcare.com') {
        setUser(session.user);
        const { data } = await supabase.from('tankcare_appointments').select('*').order('created_at', { ascending: false });
        if (data) setAppointments(data);
        setLoading(false);
      }
      setAuthChecked(true);
    };
    checkSession();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: loginEmail,
        password: loginPassword,
      });
      if (error) throw error;
      if (data.user?.email !== 'admin@tankcare.com') {
        await supabase.auth.signOut();
        throw new Error('Access denied. Only admin accounts are allowed.');
      }
      setUser(data.user);
      // Fetch appointments after login
      const { data: apptData } = await supabase.from('tankcare_appointments').select('*').order('created_at', { ascending: false });
      if (apptData) setAppointments(apptData);
      setLoading(false);
    } catch (err) {
      setLoginError(err.message);
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = async () => {
    if (window.confirm('Are you sure you want to sign out?')) {
      await supabase.auth.signOut();
      setUser(null);
      setAppointments([]);
      setLoginEmail('');
      setLoginPassword('');
    }
  };
  
  const filteredAppointments = appointments.filter(a => {
    return a.customer_name?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const pendingCount = filteredAppointments.filter(a => a.status === 'pending').length;
  const completedCount = filteredAppointments.filter(a => a.status === 'completed').length;
  const totalRevenue = filteredAppointments.reduce((sum, a) => sum + (a.status === 'completed' && a.revenue ? Number(a.revenue) : 0), 0);
  
  const handleMarkCompleted = async (id, revenueVal) => {
    const { error } = await supabase.from('tankcare_appointments').update({ status: 'completed', revenue: revenueVal || 0 }).eq('id', id);
    if (!error) {
      setAppointments(appointments.map(a => a.id === id ? { ...a, status: 'completed', revenue: revenueVal || 0 } : a));
      setIsModalOpen(false);
      setRevenueInput('');
    }
  };
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this appointment?")) return;
    const { error } = await supabase.from('tankcare_appointments').delete().eq('id', id);
    if (!error) {
      setAppointments(appointments.filter(a => a.id !== id));
      setIsModalOpen(false);
    }
  };

  // Show nothing while checking session
  if (!authChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p className="text-on-surface-variant font-body-md">Loading...</p>
      </div>
    );
  }

  // ─── LOGIN FORM ───
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="w-full max-w-md">
          <div className="flex flex-col items-center mb-8">
            <img src={logoIcon} alt="TankCare" className="w-14 h-14 object-contain mb-4" />
            <h1 className="font-display-lg text-display-lg-mobile text-on-surface mb-2">Admin Login</h1>
            <p className="font-body-md text-on-surface-variant text-center">Sign in to access the TankCare admin dashboard.</p>
          </div>
          <div className="bg-surface-container-lowest border border-surface-variant rounded-2xl p-6 md:p-8 shadow-[0_4px_30px_rgba(0,0,0,0.06)]">
            <form onSubmit={handleLogin} className="space-y-5">
              {loginError && (
                <div className="flex items-center gap-3 p-4 rounded-xl border bg-[#FCE8E6] border-[#D93025]/20 text-[#D93025]">
                  <span className="material-symbols-outlined text-[20px]">error</span>
                  <span className="font-body-md text-sm">{loginError}</span>
                </div>
              )}
              <div>
                <label className="block font-label-sm text-label-sm text-on-surface-variant uppercase tracking-wider mb-2">Email</label>
                <div className="relative">
                  <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-on-surface-variant text-[20px]">mail</span>
                  <input
                    type="email"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    placeholder="admin@tankcare.com"
                    className="w-full pl-12 pr-4 py-3.5 border border-outline-variant rounded-xl bg-surface-bright focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 font-body-md text-on-surface transition-all"
                    required
                    autoComplete="email"
                  />
                </div>
              </div>
              <div>
                <label className="block font-label-sm text-label-sm text-on-surface-variant uppercase tracking-wider mb-2">Password</label>
                <div className="relative">
                  <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-on-surface-variant text-[20px]">lock</span>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="w-full pl-12 pr-12 py-3.5 border border-outline-variant rounded-xl bg-surface-bright focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 font-body-md text-on-surface transition-all"
                    required
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-on-surface-variant hover:text-on-surface transition-colors"
                  >
                    <span className="material-symbols-outlined text-[20px]">{showPassword ? 'visibility_off' : 'visibility'}</span>
                  </button>
                </div>
              </div>
              <button
                type="submit"
                disabled={loginLoading}
                className="w-full bg-primary text-on-primary py-4 rounded-xl font-label-lg text-label-lg tracking-wide hover:bg-primary/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 mt-2"
              >
                {loginLoading ? (
                  <>
                    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Signing in...
                  </>
                ) : (
                  <>
                    <span className="material-symbols-outlined text-[20px]">login</span>
                    Sign In
                  </>
                )}
              </button>
            </form>
          </div>
          <p className="text-center mt-6 text-on-surface-variant text-sm font-body-md">
            <Link to="/" className="text-primary hover:underline">← Back to Home</Link>
          </p>
        </div>
      </div>
    );
  }

  // ─── ADMIN DASHBOARD ───
  const userName = user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'Admin';
  const capitalizedName = userName.charAt(0).toUpperCase() + userName.slice(1);

  return (
    <>
{/* Main Content */}
<main className="pt-8 flex-grow w-full max-w-container-max mx-auto px-margin-mobile md:px-margin-desktop py-8 md:py-12">
<Reveal>
<div className="flex flex-col gap-6">
{/* Dashboard Header */}
<div className="flex flex-col sm:flex-row justify-between sm:items-end gap-4 mb-4">
<div>
<h1 className="font-headline-md text-headline-md text-on-surface mb-2">Welcome back, {capitalizedName}.</h1>
<p className="font-body-md text-on-surface-variant mb-6">Here is your Dashboard Overview.</p>
<p className="text-on-surface-variant">Real-time metrics and service status.</p>
</div>
<div className="flex items-center gap-2">
<Link to="/admin/reset-password" className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-outline-variant bg-surface-container-lowest hover:bg-surface-container-low text-on-surface-variant hover:text-primary transition-all text-sm font-medium group">
<span className="material-symbols-outlined text-[18px] group-hover:text-primary transition-colors">lock_reset</span>
Reset Password
</Link>
<button onClick={handleLogout} className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-error/20 bg-surface-container-lowest hover:bg-error-container text-on-surface-variant hover:text-error transition-all text-sm font-medium group">
<span className="material-symbols-outlined text-[18px] group-hover:text-error transition-colors">logout</span>
Sign Out
</button>
</div>
</div>
{/* KPI Row */}
<div className="grid grid-cols-1 sm:grid-cols-3 gap-gutter">
{/* KPI Card 1 */}
<div className="bg-surface-container-lowest border border-surface-variant rounded-xl p-6 card-shadow relative overflow-hidden">
<div className="flex justify-between items-start mb-4">
<div>
<p className="font-label-sm text-label-sm text-on-surface-variant uppercase tracking-wider mb-1">Total Revenue</p>
<h2 className="font-headline-md text-headline-md text-on-surface">₹{totalRevenue.toLocaleString()}</h2>
</div>
<div className="bg-primary-container/10 p-2 rounded-full">
<span className="material-symbols-outlined text-primary-container">payments</span>
</div>
</div>
<div className="flex items-center gap-2 text-primary-container">
<span className="material-symbols-outlined text-[16px]">check_circle</span>
<span className="font-label-sm text-label-sm">From completed services</span>
</div>
</div>
{/* KPI Card 2 */}
<div className="bg-surface-container-lowest border border-surface-variant rounded-xl p-6 card-shadow">
<div className="flex justify-between items-start mb-4">
<div>
<p className="font-label-sm text-label-sm text-on-surface-variant uppercase tracking-wider mb-1">Tanks Cleaned</p>
<h2 className="font-headline-md text-headline-md text-on-surface">{completedCount}</h2>
</div>
<div className="bg-primary/10 p-2 rounded-full">
<span className="material-symbols-outlined text-primary">water_drop</span>
</div>
</div>
<div className="flex items-center gap-2 text-primary">
<span className="material-symbols-outlined text-[16px]">task_alt</span>
<span className="font-label-sm text-label-sm">Total completed</span>
</div>
</div>
{/* KPI Card 3 */}
<div className="bg-surface-container-lowest border border-surface-variant rounded-xl p-6 card-shadow">
<div className="flex justify-between items-start mb-4">
<div>
<p className="font-label-sm text-label-sm text-on-surface-variant uppercase tracking-wider mb-1">Pending Appointments</p>
<h2 className="font-headline-md text-headline-md text-on-surface">{pendingCount}</h2>
</div>
<div className="bg-secondary/10 p-2 rounded-full">
<span className="material-symbols-outlined text-secondary">pending_actions</span>
</div>
</div>
<div className="flex items-center gap-2 text-on-surface-variant">
<span className="material-symbols-outlined text-[16px]">schedule</span>
<span className="font-label-sm text-label-sm">Awaiting service</span>
</div>
</div>
</div>
{/* Data Grid */}
<div className="bg-surface-container-lowest border border-surface-variant rounded-xl card-shadow mt-4 overflow-hidden">
<div className="p-4 md:p-6 border-b border-surface-variant flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
<h3 className="font-body-lg text-body-lg text-on-surface font-medium">Appointments</h3>
<div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full md:w-auto">
<div className="relative w-full md:w-64">
<span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-on-surface-variant text-[18px]">search</span>
<input className="pl-10 pr-4 py-2 border border-outline-variant rounded-lg bg-surface-bright focus:outline-none focus:border-primary-container focus:ring-1 focus:ring-primary-container font-body-md text-body-md w-full" placeholder="Search clients..." type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
</div>
</div>
</div>
<div className="overflow-x-auto">
<table className="w-full text-left border-collapse">
<thead>
<tr className="border-b border-surface-variant bg-surface-bright">
<th className="p-4 font-label-sm text-label-sm text-on-surface-variant uppercase tracking-wider font-semibold">Client Name</th>
<th className="p-4 font-label-sm text-label-sm text-on-surface-variant uppercase tracking-wider font-semibold text-right w-16">Actions</th>
</tr>
</thead>
    <tbody className="divide-y divide-outline/10">
      {loading ? (
        <tr><td colSpan="2" className="p-8 text-center text-on-surface-variant">Loading appointments...</td></tr>
      ) : filteredAppointments.length === 0 ? (
        <tr><td colSpan="2" className="p-8 text-center text-on-surface-variant">
          <div className="flex flex-col items-center gap-2 opacity-60">
            <span className="material-symbols-outlined text-[32px]">inbox</span>
            <p className="font-body-md text-body-md">No appointments found</p>
          </div>
        </td></tr>
      ) : filteredAppointments.map(appt => (
        <tr key={appt.id} className="hover:bg-surface-container-lowest transition-colors">
          <td className="px-6 py-4 font-body-md font-semibold text-on-surface whitespace-nowrap">
            {appt.customer_name}
          </td>
          <td className="px-6 py-4 text-right">
            <button onClick={() => { setSelectedAppt(appt); setRevenueInput(appt.revenue || ''); setIsModalOpen(true); }} className="text-on-surface-variant hover:text-primary transition-colors p-2 rounded-full hover:bg-surface-container">
              <span className="material-symbols-outlined">more_vert</span>
            </button>
          </td>
        </tr>
      ))}
    </tbody>
    
</table>
</div>
</div>
</div>
</Reveal>
</main>
{/* Modal */}
{isModalOpen && selectedAppt && (
  <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
    <div className="bg-surface-container-lowest rounded-xl max-w-md w-full p-6 relative">
      <button onClick={() => { setIsModalOpen(false); setRevenueInput(''); }} className="absolute top-4 right-4 text-on-surface-variant hover:text-error transition-colors">
        <span className="material-symbols-outlined">close</span>
      </button>
      <h2 className="font-headline-md text-headline-md text-on-surface mb-4">Appointment Details</h2>
      <div className="space-y-4 font-body-md text-on-surface mb-6">
        <p><span className="font-semibold text-on-surface-variant">Client:</span> {selectedAppt.customer_name}</p>
        <p><span className="font-semibold text-on-surface-variant">Email:</span> {selectedAppt.customer_email}</p>
        <p><span className="font-semibold text-on-surface-variant">Phone:</span> {selectedAppt.customer_phone}</p>
        <p><span className="font-semibold text-on-surface-variant">Location:</span> {selectedAppt.address || 'N/A'}</p>
        <p><span className="font-semibold text-on-surface-variant">Date:</span> {selectedAppt.appointment_date} at {selectedAppt.appointment_time}</p>
        <p>
          <span className="font-semibold text-on-surface-variant">Status:</span> 
          <span className={`ml-2 text-xs font-bold px-2 py-1 rounded-md uppercase tracking-wider ${selectedAppt.status === 'completed' ? 'bg-primary/10 text-primary' : 'bg-error-container text-on-error-container'}`}>
            {selectedAppt.status}
          </span>
        </p>
      </div>
      
      <div className="flex flex-col gap-4 border-t border-outline-variant pt-4 mt-2">
        <div>
          <label className="block font-label-sm text-on-surface-variant mb-1">
            {selectedAppt.status === 'completed' ? 'Update Revenue (₹)' : 'Enter Revenue (₹)'}
          </label>
          <input type="number" value={revenueInput} onChange={(e) => setRevenueInput(e.target.value)} className="w-full px-4 py-2 border border-outline-variant rounded-lg focus:outline-none focus:border-primary-container" placeholder="e.g. 299" />
        </div>
        <div className="flex gap-2">
          <button onClick={() => handleMarkCompleted(selectedAppt.id, revenueInput)} disabled={!revenueInput} className="flex-1 bg-primary text-on-primary py-3 rounded-xl font-label-lg hover:bg-primary/90 transition-colors disabled:opacity-50">
            {selectedAppt.status === 'completed' ? 'Save Changes' : 'Complete Appointment'}
          </button>
          {selectedAppt.status === 'completed' && (
            <button onClick={() => handleDelete(selectedAppt.id)} className="px-4 py-3 text-error border border-error/30 hover:bg-error-container rounded-xl font-label-lg transition-colors">
              Delete
            </button>
          )}
        </div>
      </div>
    </div>
  </div>
)}
    </>
  );
};
export default AdminDashboard;